import { Suspense, lazy } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { AppShell } from './components/AppShell'
import { ProtectedRoute } from './components/ProtectedRoute'

// Pre-login pages — kept eagerly imported so the unauthenticated chunk
// renders without a Suspense flash. None of these pages (or anything
// they transitively import) touches the relation-config kinship engine;
// keeping them in the main chunk is therefore cheap AND satisfies the
// dispatch (#kinship-telugu-refine) requirement that the engine + its
// Telugu dictionary stay out of the /login bundle.
import { LoginPage } from './pages/auth/Login'
import { SignupPage } from './pages/auth/Signup'
import { PlayPage } from './pages/Play'
import { RelationQuizV2 } from './components/RelationQuizV2'
import { ForgotPasswordPage } from './pages/auth/ForgotPassword'
import { OnboardingPage } from './pages/auth/Onboarding'
import { TermsPage } from './pages/auth/Terms'
import { PrivacyPage } from './pages/auth/Privacy'
import { CommunityGuidelinesPage } from './pages/auth/CommunityGuidelines'
import { NotFoundPage } from './pages/NotFound'

// Authed pages — lazy-loaded so the relation-config engine (imported
// transitively by RelationPicker / Relatives / Circles* / etc.) lands
// in chunks downloaded ONLY after the user reaches a protected route.
// #kinship-telugu-refine: this split is what makes the bundle
// verification possible — the /login chunk must not contain any of
// the Dravidian reduction dictionary strings.
const HomePage              = lazy(() => import('./pages/Home').then((m) => ({ default: m.HomePage })))
const RelativesPage         = lazy(() => import('./pages/Relatives').then((m) => ({ default: m.RelativesPage })))
const ProfilePage           = lazy(() => import('./pages/Profile').then((m) => ({ default: m.ProfilePage })))
const ProfileEditPage       = lazy(() => import('./pages/ProfileEdit').then((m) => ({ default: m.ProfileEditPage })))
const SearchPage            = lazy(() => import('./pages/Search').then((m) => ({ default: m.SearchPage })))
const RequestsPage          = lazy(() => import('./pages/Requests').then((m) => ({ default: m.RequestsPage })))
const SentRequestsPage      = lazy(() => import('./pages/SentRequests').then((m) => ({ default: m.SentRequestsPage })))
const TreePage              = lazy(() => import('./pages/Tree').then((m) => ({ default: m.TreePage })))
const NotificationsPage     = lazy(() => import('./pages/Notifications').then((m) => ({ default: m.NotificationsPage })))
// #web-remove-messages-menu — Chat list route removed (the
// conversation list now lives only under Alerts → Messages tab).
// /chat/:userId detail route stays.
const ChatDetailPage        = lazy(() => import('./pages/ChatDetail').then((m) => ({ default: m.ChatDetailPage })))
const CirclesPage           = lazy(() => import('./pages/Circles').then((m) => ({ default: m.CirclesPage })))
const CirclesCreatePage     = lazy(() => import('./pages/CirclesCreate').then((m) => ({ default: m.CirclesCreatePage })))
const CirclesEditPage       = lazy(() => import('./pages/CirclesEdit').then((m) => ({ default: m.CirclesEditPage })))
const CircleMembersPage     = lazy(() => import('./pages/CircleMembers').then((m) => ({ default: m.CircleMembersPage })))
const SuggestionsPage       = lazy(() => import('./pages/Suggestions').then((m) => ({ default: m.SuggestionsPage })))
const PostComposePage       = lazy(() => import('./pages/PostCompose').then((m) => ({ default: m.PostComposePage })))
const SettingsPage          = lazy(() => import('./pages/Settings').then((m) => ({ default: m.SettingsPage })))
const MyReportsPage         = lazy(() => import('./pages/MyReports').then((m) => ({ default: m.MyReportsPage })))
const DeleteAccountPage     = lazy(() => import('./pages/DeleteAccount').then((m) => ({ default: m.DeleteAccountPage })))
const ConnectionsPage       = lazy(() => import('./pages/Connections').then((m) => ({ default: m.ConnectionsPage })))
const ConnectionsCommonPage = lazy(() => import('./pages/ConnectionsCommon').then((m) => ({ default: m.ConnectionsCommonPage })))
const ConnectionsBetweenPage = lazy(() => import('./pages/ConnectionsBetween').then((m) => ({ default: m.ConnectionsBetweenPage })))
const DependentsPage        = lazy(() => import('./pages/Dependents').then((m) => ({ default: m.DependentsPage })))
const DependentsAddPage     = lazy(() => import('./pages/DependentsAdd').then((m) => ({ default: m.DependentsAddPage })))
const DependentsEditPage    = lazy(() => import('./pages/DependentsEdit').then((m) => ({ default: m.DependentsEditPage })))

// Admin pages — also lazy so the admin console + its data tables stay
// out of the main authed chunk and only ship when admins actually visit.
const AdminLayout    = lazy(() => import('./components/AdminLayout').then((m) => ({ default: m.AdminLayout })))
const AdminRoute     = lazy(() => import('./components/AdminRoute').then((m) => ({ default: m.AdminRoute })))
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard').then((m) => ({ default: m.AdminDashboard })))
const UsersList      = lazy(() => import('./pages/admin/UsersList').then((m) => ({ default: m.UsersList })))
const UserDetail     = lazy(() => import('./pages/admin/UserDetail').then((m) => ({ default: m.UserDetail })))
const PostsList      = lazy(() => import('./pages/admin/PostsList').then((m) => ({ default: m.PostsList })))
const PostDetail     = lazy(() => import('./pages/admin/PostDetail').then((m) => ({ default: m.PostDetail })))
const ReportsList    = lazy(() => import('./pages/admin/ReportsList').then((m) => ({ default: m.ReportsList })))
const ReportDetail   = lazy(() => import('./pages/admin/ReportDetail').then((m) => ({ default: m.ReportDetail })))
const AdminAudit     = lazy(() => import('./pages/admin/AdminAudit').then((m) => ({ default: m.AdminAudit })))

// Minimal full-bleed fallback used while lazy chunks load. Intentionally
// quiet — the authed chunks are small and load in <100ms on broadband,
// so a spinner would flash longer than the actual wait.
function RouteSuspense({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center w-full h-full min-h-[200px] text-navy/40 text-sm">
          Loading…
        </div>
      }
    >
      {children}
    </Suspense>
  )
}

// URL shapes mirror the mobile Expo Router routes (with the (group)
// segments stripped). E.g.
//   mobile: /(app)/profile/[userId]   ↔  web: /profile/:userId
//   mobile: /(app)/chat/[userId]      ↔  web: /chat/:userId

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/signup', element: <SignupPage /> },
  // #play-route-web: standalone Kinship Challenge surface. Eager
  // import like the other pre-login pages so the entry chunk stays
  // self-contained and the engine (server-side via
  // POST /api/public/kinship-labels) never lands in the /play bundle.
  // #play-route-web: standalone Kinship Challenge surface. Eager
  // import like the other pre-login pages so the entry chunk stays
  // self-contained. PlayPage is a thin wrapper that handles the
  // ?source=mobile-app chrome suppression + brand header + signup
  // footer; the inner quiz body is RelationQuizV2 since
  // #kinship-quiz-cutover-route.
  { path: '/play', element: <PlayPage /> },
  // #kinship-quiz-cutover-route — canonical pre-login quiz route.
  // Renders the V2 quiz with no wrapper chrome. Mobile users land
  // here via the login-page CTA after their post-cutover deep
  // link (which still hits /play for back-compat).
  { path: '/quiz', element: <RelationQuizV2 /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/onboarding', element: <OnboardingPage /> },
  { path: '/terms', element: <TermsPage /> },
  { path: '/privacy', element: <PrivacyPage /> },
  { path: '/community-guidelines', element: <CommunityGuidelinesPage /> },

  {
    element: (
      <ProtectedRoute>
        <AppShell />
      </ProtectedRoute>
    ),
    children: [
      { path: '/',                          element: <RouteSuspense><HomePage /></RouteSuspense> },
      { path: '/relatives',                 element: <RouteSuspense><RelativesPage /></RouteSuspense> },
      { path: '/search',                    element: <RouteSuspense><SearchPage /></RouteSuspense> },
      { path: '/requests',                  element: <RouteSuspense><RequestsPage /></RouteSuspense> },
      { path: '/sent-requests',             element: <RouteSuspense><SentRequestsPage /></RouteSuspense> },
      { path: '/profile/edit',              element: <RouteSuspense><ProfileEditPage /></RouteSuspense> },
      { path: '/profile/:userId',           element: <RouteSuspense><ProfilePage /></RouteSuspense> },
      { path: '/tree/:userId',              element: <RouteSuspense><TreePage /></RouteSuspense> },
      { path: '/notifications',             element: <RouteSuspense><NotificationsPage /></RouteSuspense> },
      { path: '/suggestions',               element: <RouteSuspense><SuggestionsPage /></RouteSuspense> },
      { path: '/chat/:userId',              element: <RouteSuspense><ChatDetailPage /></RouteSuspense> },
      { path: '/circles',                   element: <RouteSuspense><CirclesPage /></RouteSuspense> },
      { path: '/circles/create',            element: <RouteSuspense><CirclesCreatePage /></RouteSuspense> },
      { path: '/circles/:id/edit',          element: <RouteSuspense><CirclesEditPage /></RouteSuspense> },
      { path: '/circles/:id/members',       element: <RouteSuspense><CircleMembersPage /></RouteSuspense> },
      { path: '/post',                      element: <RouteSuspense><PostComposePage /></RouteSuspense> },
      { path: '/settings',                  element: <RouteSuspense><SettingsPage /></RouteSuspense> },
      { path: '/reports/mine',              element: <RouteSuspense><MyReportsPage /></RouteSuspense> },
      { path: '/account/delete',            element: <RouteSuspense><DeleteAccountPage /></RouteSuspense> },
      { path: '/connections',               element: <RouteSuspense><ConnectionsPage /></RouteSuspense> },
      { path: '/connections/common',        element: <RouteSuspense><ConnectionsCommonPage /></RouteSuspense> },
      { path: '/connections/between',       element: <RouteSuspense><ConnectionsBetweenPage /></RouteSuspense> },
      { path: '/dependents',                element: <RouteSuspense><DependentsPage /></RouteSuspense> },
      { path: '/dependents/add',            element: <RouteSuspense><DependentsAddPage /></RouteSuspense> },
      { path: '/dependents/edit',           element: <RouteSuspense><DependentsEditPage /></RouteSuspense> },
    ],
  },

  // Admin console — separate layout (not AppShell). Stacks ProtectedRoute
  // (must be logged in) + AdminRoute (must have isAdmin=true). Server
  // enforces with 403 regardless; the UI guard is UX, not security.
  {
    element: (
      <ProtectedRoute>
        <RouteSuspense>
          <AdminRoute>
            <AdminLayout />
          </AdminRoute>
        </RouteSuspense>
      </ProtectedRoute>
    ),
    children: [
      { path: '/admin',                     element: <RouteSuspense><AdminDashboard /></RouteSuspense> },
      { path: '/admin/users',               element: <RouteSuspense><UsersList /></RouteSuspense> },
      { path: '/admin/users/:userId',       element: <RouteSuspense><UserDetail /></RouteSuspense> },
      { path: '/admin/posts',               element: <RouteSuspense><PostsList /></RouteSuspense> },
      { path: '/admin/posts/:postId',       element: <RouteSuspense><PostDetail /></RouteSuspense> },
      { path: '/admin/reports',             element: <RouteSuspense><ReportsList /></RouteSuspense> },
      { path: '/admin/reports/:reportId',   element: <RouteSuspense><ReportDetail /></RouteSuspense> },
      { path: '/admin/audit',               element: <RouteSuspense><AdminAudit /></RouteSuspense> },
    ],
  },

  { path: '/index.html', element: <Navigate to="/" replace /> },
  { path: '*', element: <NotFoundPage /> },
])
